// src/_services/photo.service.ts
// เก็บรูปใน RTDB เป็น base64 — resize ก่อน upload เพื่อลดขนาด

import { dbSet, dbRead } from './firebase'
import { DB_PHOTO_BY_ID, DB_PHOTO_ITEM } from '../_config/db_paths'
import { ref, remove } from 'firebase/database'
import { db } from './firebase'

export interface PhotoMeta {
  id:        string
  image:     string   // base64 (ไม่มี prefix)
  name:      string
  size:      number   // ขนาดหลัง resize (bytes)
  type:      string
  sort:      number
  createdAt: number
  uploadedBy: string
}

// ─── Resize ───────────────────────────────────────────────────
// maxSize: ด้านที่ยาวที่สุดจะไม่เกินค่านี้
// quality: 0.0 - 1.0 (0.5 ≈ ลดขนาด ~70-80% จากต้นฉบับ)
export function resizeImage(
  file: File,
  maxSize = 800,
  quality = 0.55,
): Promise<{ blob: Blob; base64: string }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img

      // ย่อให้ด้านยาวสุดไม่เกิน maxSize
      if (width > maxSize || height > maxSize) {
        if (width >= height) {
          height = Math.round(height * maxSize / width)
          width  = maxSize
        } else {
          width  = Math.round(width * maxSize / height)
          height = maxSize
        }
      }

      const canvas = document.createElement('canvas')
      canvas.width  = width
      canvas.height = height
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)

      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('resize failed')); return }
        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = reader.result as string
          const base64  = dataUrl.split(',')[1]
          resolve({ blob, base64 })
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      }, 'image/jpeg', quality)
    }
    img.onerror = reject
    img.src = url
  })
}

// ─── Upload single photo → RTDB ──────────────────────────────
export async function uploadPhoto(
  customerId: string,
  file: File,
  uid: string,
  sort: number,
  onProgress?: (pct: number) => void,
): Promise<PhotoMeta> {
  onProgress?.(10)

  const { blob, base64 } = await resizeImage(file)
  onProgress?.(60)

  const ts    = Date.now()
  const rand  = Math.random().toString(36).slice(2, 8)
  const photoId = `photo_${ts}_${rand}`

  const meta: PhotoMeta = {
    id:         photoId,
    image:      base64,
    name:       file.name,
    size:       blob.size,
    type:       'image/jpeg',
    sort,
    createdAt:  ts,
    uploadedBy: uid,
  }

  await dbSet(DB_PHOTO_ITEM(customerId, photoId), meta)
  onProgress?.(100)

  return meta
}

// ─── Upload multiple (sequential) ────────────────────────────
export async function uploadPhotos(
  customerId: string,
  files: File[],
  uid: string,
  existingCount = 0,
  onFileProgress?: (idx: number, pct: number) => void,
): Promise<PhotoMeta[]> {
  const results: PhotoMeta[] = []
  for (let i = 0; i < files.length; i++) {
    const meta = await uploadPhoto(
      customerId, files[i], uid,
      existingCount + i + 1,
      (pct) => onFileProgress?.(i, pct),
    )
    results.push(meta)
  }
  return results
}

// ─── Load photos (lazy) ───────────────────────────────────────
export async function loadPhotos(customerId: string): Promise<PhotoMeta[]> {
  const raw = await dbRead<Record<string, PhotoMeta>>(DB_PHOTO_BY_ID(customerId))
  if (!raw) return []
  return Object.values(raw).sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
}

// ─── Delete single photo ──────────────────────────────────────
export async function deletePhoto(customerId: string, photoId: string): Promise<void> {
  await remove(ref(db, DB_PHOTO_ITEM(customerId, photoId)))
}